/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Chart from "react-apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useParcels } from "../../store/useParcels";
import DropdownFilter from "../modals/DropdownFilter";
import { useCouriers } from "../../store/useCouriers";

// Extend dayjs with weekOfYear plugin
dayjs.extend(weekOfYear);

interface Props {
  periodProp?: "Daily" | "Weekly" | "LastTwelweMonths" | "Monthly" | "Yearly";
  selectedFiltersProp?: string[];
  weeklyRangeProp?: Date;
  monthRangeProp?: Date;
  yearRangeProp?: Date;
  className?: string;
}

export const ParcelsRevenue = React.memo(
  ({
    periodProp,
    selectedFiltersProp,
    weeklyRangeProp,
    monthRangeProp,
    yearRangeProp,
    className,
  }: Props) => {
    /** Refs */
    const snapshotRef = useRef(null);
    /** Hooks */
    const { parcelsRevenue, getParcelsRevenue } = useParcels();
    const { getAllCouriers, allCouriers } = useCouriers();
    const [_, takeScreenshot] = useScreenshot();
    /** State */
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const [period, setPeriod] = useState<"Monthly" | "Yearly">("Monthly");
    const [yearRange, setYearRange] = useState(new Date());

    /** Effects */
    useEffect(() => {
      getAllCouriers();
    }, []);

    useEffect(() => {
      setSelectedFilters([
        "All",
        ...(allCouriers?.length
          ? allCouriers?.map((courier) => courier.name as string)
          : []),
        "Total",
      ]);
    }, [allCouriers]);

    useEffect(() => {
      if (periodProp && periodProp !== "Monthly" && periodProp !== "Yearly") {
        // Handle other periods if needed
      } else if (periodProp === "Monthly") {
        setPeriod("Monthly");
      } else if (periodProp === "Yearly") {
        setPeriod("Yearly");
      }
      if (selectedFiltersProp) setSelectedFilters(selectedFiltersProp);
      if (yearRangeProp) setYearRange(yearRangeProp);
    }, [periodProp, selectedFiltersProp, yearRangeProp]);

    useEffect(() => {
      if (period === "Monthly") {
        getParcelsRevenue(yearRange, "monthly");
      } else if (period === "Yearly") {
        getParcelsRevenue(yearRange, "yearly");
      }
    }, [period, yearRange]);
    /** Callbacks */
    const getLastYear = () =>
      setYearRange(dayjs(yearRange).subtract(1, "year").toDate());
    const getNextYear = () =>
      setYearRange(dayjs(yearRange).add(1, "year").toDate());

    const getMonths = (date: Date) => {
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      const currentMonth = date.getMonth(); // getMonth() returns 0 for January, 11 for December

      return monthNames.slice(0, currentMonth + 1);
    };

    const monthlyTickAmount = useMemo(() => {
      const months = getMonths(yearRange);
      if (new Date().getFullYear() === yearRange.getFullYear()) {
        return months.length + 1;
      }
      return 11;
    }, [yearRange]);

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        // Create a link to download the image
        const link = document.createElement("a");
        link.href = image;
        link.download = "amount_drops_by_kg.png"; // Set the default file name
        link.click(); // Programmatically click the link to trigger download
      });
    };

    const filterOptions = useMemo(() => {
      return [
        "All",
        ...(allCouriers?.length
          ? allCouriers?.map((courier) => courier.name as string)
          : []),
        "Total",
      ];
    }, [allCouriers]);

    const onSelectFilters = useCallback(
      (option: string) => {
        if (option === "All") {
          if (selectedFilters.includes("All")) {
            setSelectedFilters([]);
            return;
          }
          setSelectedFilters([
            "All",
            ...(allCouriers?.length
              ? allCouriers?.map((courier) => courier.name as string)
              : []),
            "Total",
          ]);
          return;
        }
        setSelectedFilters((prev: any) =>
          prev.includes(option)
            ? prev
                .filter((item: any) => item !== option)
                ?.filter((item: any) => item !== "All")
            : [...prev, option]?.filter((item: any) => item !== "All")
        );
      },
      [allCouriers, selectedFilters, filterOptions]
    );

    const transformedData = useMemo(() => {
      if (!parcelsRevenue) return [];

      // Filter series based on selected filters
      const filteredSeries = parcelsRevenue.filter((series) =>
        selectedFilters.includes(series.name)
      );

      return filteredSeries ?? [];
    }, [parcelsRevenue, selectedFilters]);

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-2 sm:mb-12">
            Revenue
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end">
            {period === "Monthly" ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
                <PrimaryButton size="sm" onClick={getLastYear} label="Prev" />
                <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                  {dayjs(yearRange).format("YYYY")}
                </div>
                <PrimaryButton
                  size="sm"
                  disabled={dayjs(yearRange).add(1, "year").isAfter(dayjs())}
                  onClick={getNextYear}
                  label="Next"
                />
              </div>
            ) : null}
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {period}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  disabled={period === "Monthly"}
                  onClick={() => setPeriod("Monthly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Monthly
                </MenuItem>
                <MenuItem
                  disabled={period === "Yearly"}
                  onClick={() => setPeriod("Yearly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Yearly
                </MenuItem>
              </MenuList>
            </Menu>
            <div className="ml-0 sm:ml-2" />
            <DropdownFilter
              label="Filters"
              options={filterOptions}
              selectedOptions={selectedFilters}
              onSelect={onSelectFilters}
            />
            <div className="ml-0 sm:ml-2" />
            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
            />
          </div>
        </div>
        <div ref={snapshotRef}>
          {transformedData && period === "Monthly" ? (
            // @ts-ignore
            <Chart
              options={{
                ...options,
                xaxis: {
                  // Only show 1 tick when there's only one month
                  tickAmount: monthlyTickAmount,
                  labels: {
                    // @ts-ignore
                    formatter: function (value: number) {
                      const thisMonth = dayjs(value)
                        .add(5, "day")
                        .format("MMM");
                      const currentMonth = dayjs(new Date()).format("MMM");
                      const isSameYear =
                        yearRange.getFullYear() === new Date().getFullYear();
                      if (currentMonth === thisMonth && isSameYear) {
                        return "Past 30 days";
                      }
                      return dayjs(value).add(5, "day").format("MMM");
                    },
                  },
                },
                yaxis: {
                  title: {
                    text: `Revenue (Euro)`,
                    style: {
                      fontSize: "14px",
                      fontWeight: "bold",
                    },
                  },
                  labels: {
                    formatter: function (value: number) {
                      return Number(Number(value ?? 0) / 100);
                    },
                  },
                },
              }}
              series={transformedData}
              // type="line"
              height={300}
            />
          ) : null}
          {transformedData && period === "Yearly" ? (
            // @ts-ignore
            <Chart
              // @ts-ignore
              options={{
                ...options,
                xaxis: {
                  tickAmount: 3,
                  labels: {
                    // @ts-ignore
                    formatter: function (value: number) {
                      return dayjs(value).format("YYYY");
                    },
                  },
                },
                yaxis: {
                  title: {
                    text: `Number of Parcels`,
                    style: {
                      fontSize: "14px",
                      fontWeight: "bold",
                    },
                  },
                  labels: {
                    formatter: function (value: number) {
                      return Number(Number(value ?? 0) / 100);
                    },
                  },
                },
              }}
              series={transformedData}
              // type="line"
              height={300}
            />
          ) : null}
        </div>
      </div>
    );
  }
);

const options = {
  chart: {
    type: "line",
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  grid: {
    strokeDashArray: 5,
  },
  markers: {
    size: 1,
  },
  tooltip: {
    shared: true,
    intersect: false,
    custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
      // Create an array of series with their values
      const seriesData = series.map((seriesValues: any, index: number) => ({
        name: w.globals.seriesNames[index],
        value: seriesValues[dataPointIndex],
        color: w.config.colors[index],
      }));

      // Sort by value in descending order (highest to lowest)
      seriesData.sort((a: any, b: any) => b.value - a.value);

      // Build the tooltip HTML with original ApexCharts styling
      let tooltipHtml =
        '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">';
      tooltipHtml += `${dayjs(
        w.globals.seriesX[seriesIndex][dataPointIndex]
      ).format("MMM DD, YYYY")}</div>`;

      seriesData.forEach((item: any) => {
        const formattedValue = new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(Number(item.value ?? 0) / 100);

        tooltipHtml += `
          <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
            <span class="apexcharts-tooltip-marker" style="background-color: ${
              item.color
            };"></span>
            <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
              <div class="apexcharts-tooltip-y-group">
                <span class="apexcharts-tooltip-text-y-label">${
                  item.name
                }: </span>
                <span class="apexcharts-tooltip-text-y-value">${formattedValue}</span>
              </div>
            </div>
          </div>
        `;
      });

      return tooltipHtml;
    },
  },
  colors: [
    "#23C16B", // Green
    "#FFB323", // Yellow
    "#48A7F8", // Blue
    "#FF3333", // Red
    "#FF8800", // Orange
    "#9259FF", // Purple
    "#FF4DA6", // Pink
    "#33FFE6", // Teal
    "#808080", // Gray
    "#D17A50", // Brown
    "#FF7F50", // Coral
    "#8A2BE2", // BlueViolet
    "#5F9EA0", // CadetBlue
    "#DC143C", // Crimson
    "#00FFFF", // Cyan
    "#FF1493", // DeepPink
    "#696969", // DimGray
    "#B8860B", // DarkGoldenrod
    "#FF4500", // OrangeRed
    "#32CD32", // LimeGreen
    "#4682B4", // SteelBlue
    "#00FA9A", // MediumSpringGreen
    "#FFD700", // Gold
    "#FF6347", // Tomato
    "#7B68EE", // MediumSlateBlue
    "#008080", // Teal
    "#6A5ACD", // SlateBlue
    "#D2691E", // Chocolate
    "#B22222", // FireBrick
    "#9ACD32", // YellowGreen
    "#8B008B", // DarkMagenta
    "#00CED1", // DarkTurquoise
    "#FF69B4", // HotPink
    "#1E90FF", // DodgerBlue
    "#40E0D0", // Turquoise
    "#EE82EE", // Violet
  ],
};
